import CardLayout from "components/foundation/templates/CardLayout";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import FreeTagEditor from "../Create/CreateDocumentForm/CreateDocumentFormHeader/FreeTagEditor";
import { CheckIcon } from "assets/icons";
import SelectButton from "components/foundation/SelectButton";
import {
  selectedArchivesBusinessTagsState,
  selectedArchivesDocumentClassState,
  selectedArchivesTopicTagsState,
} from "store/document/archivesDocument";
import { useRecoilState } from "recoil";
import { DOCUMENT_UUID } from "config/constant";
import { DocumentClassDto, FreeTagDetailDto } from "apis/data-contracts";
import useTagsByDocumentCategoryQuery from "features/tag/hooks/queries/useTagsByDocumentCategoryQuery";
import Button from "components/foundation/Button";
import Divider from "components/foundation/Divider";
import Modal from "components/foundation/Modal/Modal";
import { documentFreeTagsState } from "store/document/documentCommon";
import { useUpdateDocumentTags } from "features/document/hooks/mutations/useUpdateDocumentTags";
import { useDocumentClassesByCategoryQuery } from "features/document/hooks/queries/useDocumentClassesByCategoryQuery";
import useDocumentDetailQuery from "features/document/hooks/queries/detail/useDocumentDetailQuery";

interface ArchivesTagEditSelectProps {
  uuid: string;
  freeTags?: FreeTagDetailDto[];
  documentClassDto?: DocumentClassDto;
  onClose: (isSuccess?: boolean) => void;
  onTagUpdateSuccess: () => void;
}

const ArchivesTagEditSelect = ({
  uuid,
  freeTags,
  documentClassDto,
  onClose,
  onTagUpdateSuccess,
}: ArchivesTagEditSelectProps) => {
  /* 태그 */
  const { data: archivesTags } = useTagsByDocumentCategoryQuery(
    DOCUMENT_UUID.ARCHIVES.uuid,
  );

  /* 문서 구분 */
  const { data: documentClasses } = useDocumentClassesByCategoryQuery(
    DOCUMENT_UUID.ARCHIVES.uuid,
  );

  /* 자유 태그 */
  const [selectedFreeTags, setSelectedFreeTags] = useRecoilState(
    documentFreeTagsState,
  );

  /* 태그 리스트 */
  const { data: documentDetail } = useDocumentDetailQuery(uuid);
  const tags = documentDetail?.tags;
  const BUSINESS_TAG_CATEGORY_UUID = "c9f1b214-79f1-43d2-8eb3-1659f35523b2";
  const TOPIC_TAG_CATEGORY_UUID = "a17e945b-1d3b-4477-b299-63bd2cc087eb";

  useEffect(() => {
    if (freeTags && freeTags.length === 0) {
      setSelectedFreeTags([]);
    } else if (freeTags && freeTags.length > 0) {
      setSelectedFreeTags(freeTags);
    }
  }, [freeTags, setSelectedFreeTags]);

  /* 문서 클래스 태그 */
  const [selectedDocumentClass, setSelectedDocumentClass] = useRecoilState(
    selectedArchivesDocumentClassState,
  );

  useEffect(() => {
    if (documentClassDto) {
      setSelectedDocumentClass(documentClassDto);
    }
  }, [documentClassDto, setSelectedDocumentClass]);

  // 사업구분 태그
  const [selectedBusinessTags, setSelectedBusinessTags] = useRecoilState(
    selectedArchivesBusinessTagsState,
  );

  useEffect(() => {
    if (tags && tags.length === 0) {
      setSelectedBusinessTags([]);
    } else if (tags && tags.length > 0) {
      const selectedBusinessTags = tags.filter(
        (tag) => tag.categoryUuid === BUSINESS_TAG_CATEGORY_UUID,
      );
      if (selectedBusinessTags.length > 0) {
        setSelectedBusinessTags(selectedBusinessTags);
      }
    }
  }, [tags]);

  // 주제분류 태그
  const [selectedTopics, setSelectedTopics] = useRecoilState(
    selectedArchivesTopicTagsState,
  );

  useEffect(() => {
    if (tags && tags.length === 0) {
      setSelectedTopics([]);
    } else if (tags && tags.length > 0) {
      const selectedTopics = tags.filter(
        (tag) => tag.categoryUuid === TOPIC_TAG_CATEGORY_UUID,
      );
      if (selectedTopics.length > 0) {
        setSelectedTopics(selectedTopics);
      }
    }
  }, [tags]);

  /* 완료 버튼 */
  const [isOpenModal, setIsOpenModal] = useState(false);
  const { mutate: updateDocumentTags } = useUpdateDocumentTags();

  const onClickConfirm = () => {
    setIsOpenModal(true);
  };

  const handleUpdateTags = () => {
    updateDocumentTags(
      {
        uuid: uuid,
        data: {
          documentClassUuid: selectedDocumentClass.uuid,
          tagUuids: [
            ...(selectedBusinessTags?.map((tag) => tag.tagUuid) ?? []),
            ...(selectedTopics?.map((topic) => topic.tagUuid) ?? []),
          ],
          freeTagUuids: selectedFreeTags.map((tag) => tag.uuid),
        },
      },
      {
        onSuccess: () => {
          onClose(true);
          setTimeout(() => {
            onTagUpdateSuccess();
          }, 300);
        },
      },
    );
  };

  return (
    <>
      <CardLayout.Container
        header={{
          title: "태그 수정",
          description:
            "문서에 들어갈 기본 태그를 수정 또는 추가할 수 있습니다.",
        }}
        gap={40}
        headerColor="#171D23"
      >
        <DocumentArchivesCreateForm
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <CardLayout.Field label="자유태그">
            <FreeTagEditor isTitle={false} />
          </CardLayout.Field>

          <CardLayout.Field label="문서구분">
            <SelectContainerStyle>
              {documentClasses?.map((documentClass) => {
                const selected =
                  documentClass.uuid === selectedDocumentClass?.uuid;
                return (
                  <SelectButton
                    key={documentClass.uuid}
                    type="button"
                    selected={selected}
                    rightIcon={selected && <CheckIcon />}
                    onClick={() =>
                      setSelectedDocumentClass({
                        uuid: documentClass.uuid,
                        name: documentClass.name,
                      })
                    }
                  >
                    {documentClass.name}
                  </SelectButton>
                );
              })}
            </SelectContainerStyle>
          </CardLayout.Field>

          <CardLayout.Field label="사업구분">
            <SelectContainerStyle>
              {archivesTags?.tagCategories[0].tags?.map((businessTag) => {
                const selected = selectedBusinessTags.some(
                  (item) => item.tagUuid === businessTag.tagUuid,
                );
                return (
                  <SelectButton
                    key={businessTag.tagUuid}
                    type="button"
                    value={businessTag.tagName}
                    selected={!!selected}
                    rightIcon={selected && <CheckIcon />}
                    onClick={() => {
                      const isSelected = selectedBusinessTags.some(
                        (item) => item.tagUuid === businessTag.tagUuid,
                      );
                      if (isSelected) {
                        setSelectedBusinessTags(
                          selectedBusinessTags.filter(
                            (item) => item.tagUuid !== businessTag.tagUuid,
                          ),
                        );
                      } else {
                        setSelectedBusinessTags([
                          ...selectedBusinessTags,
                          businessTag,
                        ]);
                      }
                    }}
                  >
                    {businessTag.tagName}
                  </SelectButton>
                );
              })}
            </SelectContainerStyle>
          </CardLayout.Field>

          <CardLayout.Field label="주제분류">
            <TopicSelctionContainerStyle>
              {archivesTags?.tagCategories[1].tags?.map((topic) => {
                const selected = selectedTopics.some(
                  (item) => item.tagUuid === topic.tagUuid,
                );
                return (
                  <SelectButton
                    key={topic.tagUuid}
                    type="button"
                    value={topic.tagName}
                    selected={!!selected}
                    rightIcon={selected && <CheckIcon />}
                    onClick={() => {
                      const isSelected = selectedTopics.some(
                        (item) => item.tagUuid === topic.tagUuid,
                      );
                      if (isSelected) {
                        setSelectedTopics(
                          selectedTopics.filter(
                            (item) => item.tagUuid !== topic.tagUuid,
                          ),
                        );
                      } else {
                        setSelectedTopics([...selectedTopics, topic]);
                      }
                    }}
                  >
                    {topic.tagName}
                  </SelectButton>
                );
              })}
            </TopicSelctionContainerStyle>
          </CardLayout.Field>
        </DocumentArchivesCreateForm>
        <Divider />
        <ButtonWrapperStyle>
          <Button
            variant="tertiary"
            size="large"
            fill
            onClick={() => onClose(false)}
          >
            취소
          </Button>
          <Button variant="primary" size="large" fill onClick={onClickConfirm}>
            완료
          </Button>
        </ButtonWrapperStyle>
      </CardLayout.Container>
      <Modal.Root open={isOpenModal} onOpenChange={setIsOpenModal}>
        <Modal.Body
          onConfirm={handleUpdateTags}
          onCancel={() => setIsOpenModal(false)}
          title="태그를 수정하시겠습니까?"
        ></Modal.Body>
      </Modal.Root>
    </>
  );
};

export default ArchivesTagEditSelect;

/* 스타일 */
const DocumentArchivesCreateForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 5rem;
`;

const SelectContainerStyle = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14.2rem, 1fr));
  gap: 1rem;
`;

const TopicSelctionContainerStyle = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: 1rem;
`;

const ButtonWrapperStyle = styled.div`
  display: flex;
  gap: 1rem;
`;
